import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LogElement, Order } from '@models/order';
import { FormatData } from '@classes/format-data';
import { OrderService } from '@services/order.service';

export interface RelatedInvoice {
  invoiceNumber: string;
  orderId: string;
  current?: boolean;
}

export interface OrderLogDialogData {
  logs: LogElement[];
  invoiceNumber?: string;
  linkedInvoice?: string;
  orderId?: string;
  patientId?: string;
}

@Component({
  selector: 'app-order-log-dialog',
  templateUrl: './order-log-dialog.component.html',
  styleUrls: ['./order-log-dialog.component.css'],
})
export class OrderLogDialogComponent implements OnInit {
  relatedInvoices: RelatedInvoice[] = [];

  constructor(
    public dialogRef: MatDialogRef<OrderLogDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrderLogDialogData,
    private orderService: OrderService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadRelatedInvoices();
  }

  get title(): string {
    return String(this.data?.invoiceNumber || '').trim() || 'QUOTE';
  }

  get sortedLogs(): LogElement[] {
    const logs = this.data?.logs;
    if (!logs?.length) {
      return [];
    }
    return logs.slice().sort((a, b) => {
      const aTime = new Date(a.date ?? 0).getTime();
      const bTime = new Date(b.date ?? 0).getTime();
      return aTime - bTime;
    });
  }

  formatLogItem(item?: string): string {
    return FormatData.formatLogItem(item);
  }

  hasLogTime(date?: string): boolean {
    if (!date) {
      return false;
    }
    const raw = String(date).trim();
    if (/^\d+$/.test(raw)) {
      return true;
    }
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) {
      return false;
    }
    return (
      parsed.getHours() !== 0 ||
      parsed.getMinutes() !== 0 ||
      parsed.getSeconds() !== 0
    );
  }

  openRelated(related: RelatedInvoice): void {
    if (!related?.orderId || related.current || related.orderId === this.data?.orderId) {
      return;
    }
    const patientId = this.data?.patientId;
    this.dialogRef.close();
    if (patientId) {
      this.router.navigate(['/admin/patient', patientId, 'order', related.orderId]);
    }
  }

  private loadRelatedInvoices(): void {
    const patientId = this.data?.patientId;
    const invoice = String(this.data?.invoiceNumber || '').trim();
    const root = FormatData.invoiceFamilyRoot(invoice);
    if (!patientId || !root) {
      return;
    }
    this.orderService.getPatientOrders(patientId, 'orders').subscribe({
      next: (orders: Order[]) => {
        const currentInvoice = String(this.data?.invoiceNumber || '').trim();
        const currentLinked = String(this.data?.linkedInvoice || '').trim();
        const roots = new Set(
          [FormatData.invoiceFamilyRoot(currentInvoice), FormatData.invoiceFamilyRoot(currentLinked)]
            .map((root) => String(root || '').trim())
            .filter(Boolean)
        );
        const related = (orders || [])
          .map((order) => ({
            invoiceNumber: String(order?.invoiceNumber || '').trim(),
            linkedInvoice: String((order as any)?.linkedInvoice || '').trim(),
            orderId: String(order?._id || ''),
          }))
          .filter((entry) => {
            if (!entry.invoiceNumber || !entry.orderId) {
              return false;
            }
            if (roots.has(FormatData.invoiceFamilyRoot(entry.invoiceNumber))) {
              return true;
            }
            if (roots.has(FormatData.invoiceFamilyRoot(entry.linkedInvoice))) {
              return true;
            }
            return (
              entry.linkedInvoice === currentInvoice ||
              currentLinked === entry.invoiceNumber
            );
          })
          .sort((a, b) =>
            a.invoiceNumber.localeCompare(b.invoiceNumber, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          );
        const seen = new Set<string>();
        this.relatedInvoices = related
          .filter((entry) => {
            if (seen.has(entry.orderId)) {
              return false;
            }
            seen.add(entry.orderId);
            return true;
          })
          .map((entry) => ({
            ...entry,
            current: entry.orderId === this.data?.orderId,
          }));
      },
      error: () => {
        this.relatedInvoices = [];
      },
    });
  }
}
